import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import {
  AirplaneTilt,
  Car,
  Drone,
  Leaf,
  Compass,
  ArrowRight,
  Sparkle,
  Lightning,
  Microphone,
  Robot,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import LiveTVHeroStrip from "../components/LiveTVHeroStrip";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/30010fec-5a02-4943-b47d-5647753361a6/images/494c95d51de91c03973d93e4f00722be189abcdd38820fdc04f5640b3e5b6587.jpeg";

const HUBS = [
  { key: "aerospace", name: "Aerospace", icon: AirplaneTilt, color: "#00E5FF", short: "AER",
    line: "Propulsion · avionics · deep space" },
  { key: "automotive", name: "Automotive", icon: Car, color: "#E60012", short: "AUT",
    line: "EVs · autonomy · racing legacy" },
  { key: "uam", name: "Urban Air Mobility", icon: Drone, color: "#FFAB00", short: "UAM",
    line: "eVTOL · drones · vertiports" },
  { key: "sustainability", name: "Sustainability", icon: Leaf, color: "#00E676", short: "SUS",
    line: "Hydrogen · SAF · circular materials" },
  { key: "stories", name: "Travel Stories", icon: Compass, color: "#FFFFFF", short: "STR",
    line: "Public voices · field reports" },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user && user.id ? "/feed" : "/register";
  const [agents, setAgents] = useState([]);
  useEffect(() => { api.get("/agents").then((r) => setAgents(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#0A0A0C] border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Logo size={30} />
          <nav className="hidden md:flex items-center gap-8 label-mono">
            <a href="#hubs" className="hover:text-white transition">Hubs</a>
            <a href="#how" className="hover:text-white transition">How it works</a>
            <a href="#mission" className="hover:text-white transition">Mission</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="btn btn-ghost btn-sm" data-testid="landing-signin-btn">Sign in</Link>
            <Link to={ctaTo} className="btn btn-primary btn-sm" data-testid="landing-join-btn">
              Join iAAM <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Cinematic aerial backdrop — slow drone push-in */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" data-testid="hero-cinematic-backdrop">
          <div className="hero-kenburns absolute inset-0">
            <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/80 to-[#0A0A0C]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-[#0A0A0C]/70" />
          <div className="absolute inset-0 blueprint-grid opacity-40" />
          <svg className="absolute inset-0 w-full h-full opacity-60" preserveAspectRatio="none" viewBox="0 0 1200 600">
            <path className="route-line" d="M -50 430 C 250 380, 420 470, 700 400 S 1100 300, 1260 330" />
            <path className="route-line route-line-2" d="M -50 180 C 300 220, 600 120, 900 190 S 1150 160, 1260 120" />
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-12 reveal">
            <LiveTVHeroStrip />
          </div>
          <div className="lg:col-span-7 reveal">
            <div className="flex items-center gap-2 mb-8">
              <span className="hub-dot pulse" style={{ color: "#E60012" }} />
              <span className="label-mono">iAAM · COMMUNITY 001 · LIVE</span>
            </div>
            <h1 className="font-display text-white" style={{ fontSize: "clamp(40px, 7vw, 96px)", fontWeight: 900, lineHeight: 0.95, letterSpacing: "-0.045em" }}>
              The minds<br />
              <span style={{ color: "#E60012" }}>building flight</span><br />
              meet the minds<br />
              <span className="text-zinc-400">building the road.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-300 max-w-xl leading-relaxed">
              iAAM is the video-first knowledge community where aerospace engineers, automotive
              innovators, urban air mobility researchers and everyday travelers converge — to make
              sustainable mobility actually move.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to={ctaTo} className="btn btn-primary" data-testid="hero-primary-cta">
                Join the community <ArrowRight size={14} weight="bold" />
              </Link>
              <a href="#hubs" className="btn" data-testid="hero-secondary-cta">Explore the 5 hubs</a>
            </div>

            {/* Stats strip */}
            <div className="mt-14 grid grid-cols-3 border border-[#27272A]">
              {[
                { v: "5", l: "Mobility hubs" },
                { v: "AI", l: "Transcribed videos" },
                { v: "0%", l: "Algorithmic noise" },
              ].map((s, i) => (
                <div key={i} className={`p-5 ${i < 2 ? "border-r border-[#27272A]" : ""}`}>
                  <div className="font-display text-3xl text-white" style={{ fontWeight: 900 }}>{s.v}</div>
                  <div className="label-mono mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative" data-testid="hero-visual-column">
            <div className="hidden lg:flex absolute top-2 right-0 label-mono bg-black/60 border border-[#27272A] px-2.5 py-1.5 items-center gap-2">
              <span className="hub-dot pulse" style={{ color: "#00E5FF" }} /> AERIAL FEED · GOLDEN HOUR
            </div>
            <div className="hidden lg:block absolute bottom-2 right-0 label-mono bg-black/60 px-2 py-1" style={{ color: "#E60012" }}>
              IAAM/001
            </div>

            {/* floating mini card */}
            <div className="hidden lg:block absolute bottom-16 -left-8 w-72 card p-4 reveal shadow-panel" style={{ animationDelay: "200ms" }}>
              <div className="label-mono mb-2 flex items-center gap-1.5" style={{ color: "#E60012" }}>
                <Sparkle size={10} weight="fill" /> AI SUMMARY
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed">
                "Hybrid-electric regional jets are bottlenecked on thermal envelopes, not propulsion. The next 18 months are decisive."
              </p>
              <div className="mt-3 flex items-center gap-2 label-mono">
                <span>FROM A 6m VIDEO POST</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-[#27272A] py-3 overflow-hidden bg-[#121214]">
          <div className="marquee label-mono text-zinc-400">
            {Array.from({ length: 2 }).flatMap(() => [
              "FY26 SAF BLEND TARGET · 10%",
              "VERTIPORT NOISE CEILING · 65dBA @ 100m",
              "SOLID-STATE GIGAFACTORY YIELD · STILL FRAGILE",
              "eVTOL CERTIFICATION · EASA SC-VTOL TRACKED",
              "REGIONAL HYBRID · 250nm SWEET SPOT",
              "PUBLIC TRAVEL STORIES · OPEN MIC",
            ]).map((t, i) => (
              <span key={i} className="flex items-center gap-3">
                <span className="hub-dot" style={{ color: "#E60012" }} />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Hubs */}
      <section id="hubs" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-4">
            <div className="label-mono mb-3" style={{ color: "#E60012" }}>SECTION 02 · HUBS</div>
            <h2 className="font-display text-white" style={{ fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>
              Five hubs.<br />One trajectory.
            </h2>
          </div>
          <div className="lg:col-span-8 lg:pt-8">
            <p className="text-zinc-300 text-lg leading-relaxed">
              Mobility doesn't fit neatly into a single discipline anymore. iAAM is structured around five
              interlocking hubs — each with its own community, its own pace, and its own signal-to-noise discipline.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#27272A]">
          {HUBS.map((h) => (
            <Link
              key={h.key}
              to={`/hub/${h.key}`}
              className="group p-8 border-r border-b border-[#27272A] bg-[#121214] hover:bg-[#1A1A1D] transition-colors block"
              data-testid={`landing-hub-${h.key}`}
            >
              <div className="flex items-center justify-between mb-6">
                <h.icon size={32} weight="light" style={{ color: h.color }} />
                <span className="label-mono" style={{ color: h.color }}>{h.short}</span>
              </div>
              <div className="font-display text-2xl text-white mb-1.5" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                {h.name}
              </div>
              <div className="text-sm text-zinc-400">{h.line}</div>
              <div className="mt-6 label-mono flex items-center gap-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                Enter hub <ArrowRight size={10} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* AI Agents */}
      <section className="border-y border-[#27272A] bg-[#0A0A0C] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <div className="label-mono mb-3" style={{ color: "#E60012" }}>SECTION 02.5 · AI PERSONAS</div>
              <h2 className="font-display text-white" style={{ fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>
                Nine agents.<br />Always on shift.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-8">
              <p className="text-zinc-300 text-lg leading-relaxed">
                iAAM ships with a roster of AI personas — from <span className="text-white">EV Technician</span> to <span className="text-white">Aerospace Mobility</span> to <span className="text-white">Emergency Roadside</span>. They reply to community posts in their domain, answer your 1:1 questions, and tag each other in when a thread crosses disciplines.
              </p>
              <Link to="/agents" className="btn btn-primary mt-6" data-testid="landing-meet-agents-btn">
                <Robot size={14} weight="bold" /> Meet the agents <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          </div>

          {agents.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#27272A]" data-testid="landing-agents-grid">
              {agents.slice(0, 6).map((a) => (
                <Link key={a.id} to={`/agent/${a.key}`} className="bg-[#0A0A0C] hover:bg-[#121214] transition-colors group">
                  <div className="aspect-square overflow-hidden bg-[#121214]" style={{ borderTop: `2px solid ${a.color}` }}>
                    {a.avatar_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${a.avatar_url}`} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display text-5xl" style={{ background: a.color, color: "#000", fontWeight: 900 }}>
                        {a.name[0]}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="label-mono mb-1" style={{ color: a.color, fontSize: 9 }}>{a.role.replace(" Agent", "").toUpperCase()}</div>
                    <div className="text-sm text-white font-medium truncate">{a.name}</div>
                    <div className="flex items-center gap-1 mt-1 label-mono" style={{ fontSize: 9, color: "#00E676" }}>
                      <ShieldCheck size={9} weight="fill" /> {a.trust_score}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-[#27272A] bg-[#121214]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="label-mono mb-3" style={{ color: "#E60012" }}>SECTION 03 · PROTOCOL</div>
          <h2 className="font-display text-white mb-16" style={{ fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>
            Drop a video. We do the rest.
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-[#27272A]">
            {[
              { n: "01", t: "Record or write", d: "Drop a 60s video, a voice note, a photo from the test floor, or just text. Whatever fits the thought.", icon: Microphone },
              { n: "02", t: "AI transcribes", d: "OpenAI Whisper transcribes every audio + video post. GPT generates a 2-sentence summary so nothing gets buried.", icon: Sparkle },
              { n: "03", t: "Community moves", d: "Threaded replies, points for engagement, leaderboard for builders. No algorithms, no doomscroll.", icon: Lightning },
            ].map((s) => (
              <div key={s.n} className="bg-[#0A0A0C] p-8">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-display text-5xl text-zinc-700" style={{ fontWeight: 900 }}>{s.n}</span>
                  <s.icon size={24} weight="light" style={{ color: "#E60012" }} />
                </div>
                <h3 className="font-display text-2xl text-white mb-2" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>{s.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section id="mission" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="label-mono mb-3" style={{ color: "#E60012" }}>SECTION 04 · MISSION</div>
          <h2 className="font-display text-white" style={{ fontSize: "clamp(32px, 4vw, 56px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>
            Community-Driven Sustainable Future Mobility
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-6">
          <p className="text-lg text-zinc-300 leading-relaxed mb-10">
            iAAM is cultivating a collaborative smart mobility ecosystem where industry experts,
            technicians, innovators, and sector communities interact, learn, observe the intelligent
            transformation of transportation corridors.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={ctaTo} className="btn btn-primary" data-testid="mission-cta">
              Reserve your handle <ArrowRight size={14} weight="bold" />
            </Link>
            <Link to="/feed" className="btn" data-testid="mission-explore-cta">Browse the feed</Link>
          </div>
        </div>
      </section>

      {/* PUNCH AI promo */}
      <section id="punch-ai" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="punch-ai-promo">
        <div className="border border-[#27272A] grid lg:grid-cols-12 overflow-hidden bg-[#0A0A0C]">
          <div className="lg:col-span-5 relative bg-[#000] flex items-center justify-center">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/promos/punch-hero.png`}
              alt="PUNCH AI — Smart Future Mobility"
              className="w-full h-full object-contain max-h-[520px]"
              data-testid="punch-promo-image"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-transparent via-transparent to-[#0A0A0C]/40" />
          </div>
          <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center">
            <div className="label-mono mb-3 flex items-center gap-2" style={{ color: "#E60012" }}>
              <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: "#00E676" }} />
              SECTION 05 · PUNCH AI · LIVE
            </div>
            <h2 className="font-display text-white mb-4" style={{ fontSize: "clamp(28px, 3.6vw, 48px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              Intelligent movement.<br /><span style={{ color: "#E60012" }}>Powered by PUNCH.</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-6">
              PUNCH AI is the agentic mobility intelligence embedded in iAAM.one. Not a chatbot — a
              persistent, context-aware operational agent that coordinates dispatch, fleet ops, route
              optimization, and AI business onboarding across the platform.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-zinc-300 mb-8 list-none p-0">
              {[
                "Dispatch intelligence",
                "EV fleet operations",
                "Predictive mobility analytics",
                "Autonomous workflow coordination",
                "AI business onboarding",
                "Multi-stop route optimization",
              ].map((cap) => (
                <li key={cap} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full" style={{ background: "#E60012" }} />
                  {cap}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <Link to="/agent/punch-ai" className="btn btn-primary" data-testid="punch-cta-chat">
                Talk to PUNCH AI <ArrowRight size={14} weight="bold" />
              </Link>
              <Link to="/agents" className="btn" data-testid="punch-cta-agents">All agents</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
          <Logo size={24} />
          <span className="label-mono">© 2026 · INTEGRATION OF AEROSPACE & AUTOMOTIVE MOBILITY</span>
          <span className="label-mono">POWERED BY <span style={{ color: "#E60012" }}>AIRDROPS PUNCH</span></span>
        </div>
      </footer>
    </div>
  );
}
